import { useEffect, useState } from 'react'
import { IGuarantorOption } from 'components/modals/guarantorPicker'

export interface IRequestTarget {
    poolship_id: number
    name: string
}

interface IStoredDraft {
    target?: IRequestTarget
    amount: string
    repayments: string
    priority: string
    note: string
    guarantors: IGuarantorOption[]
    cardId?: number
}

const key = (moneypoolId?: string) => `hamyan:loan-request-draft:${moneypoolId ?? ''}`

/**
 * The wizard's answers, kept across an unmount.
 *
 * Same trap the grant wizard hit: the review step can send the member off to
 * `/manage-bank-cards/add` when they have no registered account, which
 * unmounts the wizard — and on the way back a "nothing answered" guard would
 * bounce them to step 1 with every question gone.
 *
 * **sessionStorage, restored in a `useState` initialiser, not an effect.** The
 * step lives in the URL, so on the first render back the wizard is already at
 * `?step=4`; a draft that only lands on the second render lets the guard fire
 * first and redirect anyway.
 */
export default function useRequestDraft(moneypoolId?: string) {
    const stored = (): Partial<IStoredDraft> => {
        try {
            const raw = sessionStorage.getItem(key(moneypoolId))
            return raw ? JSON.parse(raw) : {}
        } catch {
            // a corrupt or unavailable store must not take the page down
            return {}
        }
    }

    const initial = stored()
    const [target, setTarget] = useState<IRequestTarget | undefined>(
        initial.target
    )
    const [amount, setAmount] = useState(initial.amount ?? '')
    const [repayments, setRepayments] = useState(initial.repayments ?? '')
    const [priority, setPriority] = useState(initial.priority ?? 'LOW')
    const [note, setNote] = useState(initial.note ?? '')
    const [guarantors, setGuarantors] = useState<IGuarantorOption[]>(
        initial.guarantors ?? []
    )
    const [cardId, setCardId] = useState<number | undefined>(initial.cardId)

    useEffect(() => {
        try {
            sessionStorage.setItem(
                key(moneypoolId),
                JSON.stringify({
                    target,
                    amount,
                    repayments,
                    priority,
                    note,
                    guarantors,
                    cardId,
                })
            )
        } catch {
            // storage full or blocked — the wizard still works in-memory
        }
    }, [
        moneypoolId,
        target,
        amount,
        repayments,
        priority,
        note,
        guarantors,
        cardId,
    ])

    const clear = () => {
        try {
            sessionStorage.removeItem(key(moneypoolId))
        } catch {
            /* nothing to clean up */
        }
    }

    return {
        target,
        setTarget,
        amount,
        setAmount,
        repayments,
        setRepayments,
        priority,
        setPriority,
        note,
        setNote,
        guarantors,
        setGuarantors,
        cardId,
        setCardId,
        clear,
    }
}
